import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import "../i18n";
import { useThemeSettings } from "../api/queries";
import { brandName as resolveName, brandTagline } from "../design/brand";
import { Icon } from "../design/components";
import { DISABLED_HINT, HUB_NAV, activeNavKey, visibleNav, type NavGroup, type NavItem } from "./nav";
import type { Role, Surface } from "../api/types";
import { useRail } from "./useRail";

/** `nav.ts` keeps its English `name` as the untranslated fallback (still used by other
    consumers, e.g. `itemTitle`'s aria title and any test asserting on it directly); the
    hub surface's rendered label looks up `nav.<key>` and falls back to that English name.
    Portal keys ("documents", "users") collide with hub keys, so only hub translates —
    the portal is out of scope for Phase 2 (see the plan's "Portal scope" decision). */
function useNavLabel(surface: Surface): (item: NavItem) => string {
  const { t } = useTranslation();
  return (item) => (surface === "hub" ? t(`nav.${item.key}`, item.name) : item.name);
}

function itemTitle(item: NavItem, label: string, hint: string, count?: number): string {
  if (item.disabled) return hint;
  return count === undefined ? label : `${label} (${count})`;
}

function NavEntry({
  item, count, active, surface,
}: {
  item: NavItem; count?: number | undefined; active: boolean; surface: Surface;
}) {
  const { t } = useTranslation();
  const label = useNavLabel(surface)(item);
  const hint = surface === "hub" ? t("nav.disabledHint", DISABLED_HINT) : DISABLED_HINT;
  const base = item.parent ? "nav-item nav-child" : "nav-item";
  const tag = item.badge === "entityCount" && count !== undefined ? <span className="nav-tag">{count}</span> : null;
  const body = (
    <>
      <Icon name={item.icon} />
      <span>{label}</span>
      {tag}
    </>
  );

  if (item.disabled) {
    return (
      <span className={`${base} is-disabled`} aria-disabled="true" title={hint}>
        {body}
      </span>
    );
  }
  return (
    <Link
      className={active ? `${base} is-active` : base}
      to={item.to}
      aria-current={active ? "page" : undefined}
      title={itemTitle(item, label, hint, item.badge ? count : undefined)}
    >
      {body}
    </Link>
  );
}

export function Sidebar({
  groups = HUB_NAV,
  role = "group_admin",
  surface = "hub",
  entityCount,
}: {
  groups?: NavGroup[];
  role?: Role;
  surface?: Surface;
  entityCount?: number | undefined;
}) {
  const { t } = useTranslation();
  const { railed, toggle } = useRail();
  const visible = visibleNav(groups, role);
  const active = activeNavKey(visible, useLocation().pathname);
  const theme = useThemeSettings({ enabled: surface === "hub" });
  const logoUrl = surface === "hub" ? theme.data?.logoUrl : null;

  // Only the hub surface is translated in Phase 2; the portal keeps its English strings
  // (it is out of scope — see the plan's "Portal scope" decision).
  const brand = surface === "hub"
    ? { to: "/", sub: brandTagline(t, theme.data?.tagline), sections: t("brand.sectionsHub", "Hub sections") }
    : { to: "/portal", sub: "Company portal", sections: "Company sections" };
  const brandName = surface === "hub" ? resolveName(t, theme.data?.appName) : "E-Invoicing Hub";
  const brandTitle = surface === "hub" ? resolveName(t, theme.data?.appName) : "Central E-Invoicing Hub";

  return (
    <aside className="sidebar" id="app-sidebar">
      <Link className="brand" to={brand.to} title={brandTitle}>
        {logoUrl ? (
          <img className="brand-mark brand-mark-logo" src={logoUrl} alt="" />
        ) : (
          <span className="brand-mark">E</span>
        )}
        <span className="stack">
          <span className="brand-name">{brandName}</span>
          <span className="brand-sub">{brand.sub}</span>
        </span>
      </Link>
      <button
        className="railtoggle"
        type="button"
        onClick={toggle}
        aria-expanded={!railed}
        aria-controls="app-sidebar"
        title={surface === "hub"
          ? (railed ? t("sidebar.expand", "Expand the menu") : t("sidebar.collapse", "Collapse the menu"))
          : (railed ? "Expand the menu" : "Collapse the menu")}
      >
        <svg
          className="mirror-in-rtl" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden
        >
          <path d="m15 5-7 7 7 7" />
        </svg>
      </button>
      <nav className="nav" aria-label={brand.sections}>
        {visible.map((group) => (
          <div className="nav-group" key={group.label}>
            <div className="nav-label">
              {surface === "hub" ? t(`navGroups.${group.label}`, group.label) : group.label}
            </div>
            {group.items.map((item) => (
              <NavEntry
                key={item.key} item={item} count={entityCount} active={item.key === active}
                surface={surface}
              />
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
